import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import type { InputHTMLAttributes } from 'react';
import { formatDmyHm } from './datePickerModel';
import { attachDateInputSync } from './dateInputSync';
import { attachDmyTyping, writeNativeValue } from './dateTyping';
import DateFieldOverlay from './DateFieldOverlay';

/**
 * P-57c (D-232 sec.4 / QX-611) - the date + time field of the system, the twin of ui/DateInput.
 * It is still a real `<input type="datetime-local">` (same value format `YYYY-MM-DDTHH:mm`, so
 * forms, FormData and what is sent to the server are unchanged); the global DatePickerLayer opens
 * the Hebrew calendar + hour/minute columns on a click, and the browser's own text is hidden and
 * replaced by dd/mm/yyyy HH:mm whatever the browser locale. Typing in the field still works.
 */
type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

const DateTimeInput = forwardRef<HTMLInputElement, Props>(function DateTimeInput({ className, ...rest }, ref) {
  const inner = useRef<HTMLInputElement | null>(null);
  const [shown, setShown] = useState(String(rest.value ?? rest.defaultValue ?? ''));
  const [draft, setDraft] = useState<string | null>(null); // FIX-B41 (K4): digits typed so far, as a dd/mm/yyyy mask

  const setRefs = useCallback((el: HTMLInputElement | null) => {
    inner.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as { current: HTMLInputElement | null }).current = el;
  }, [ref]);

  useEffect(() => { if (rest.value !== undefined) setShown(String(rest.value)); }, [rest.value]);
  useEffect(() => {
    const el = inner.current;
    if (!el) return;
    return attachDateInputSync(el, setShown);
  }, []);
  // FIX-B41 (QX-931 / K4): hand typing is always dd/mm/yyyy, whatever the browser's segment order.
  useEffect(() => {
    const el = inner.current;
    if (!el) return;
    return attachDmyTyping(el, true, setDraft, (v) => writeNativeValue(el, v));
  }, []);

  const text = draft ?? formatDmyHm(shown);
  return (
    <span className="relative block">
      <input {...rest} ref={setRefs} type="datetime-local" className={`${className ?? 'input'} date-dmy`} />
      <DateFieldOverlay text={text} placeholder="dd/mm/yyyy hh:mm" ltr={rest.dir === 'ltr'} disabled={!!rest.disabled} />
    </span>
  );
});

export default DateTimeInput;
